/**
 * AccessReports.js — Relatórios de Acesso — Clube Social e Condomínio
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Abas:
 *   - Movimentação Geral   (plate_logs com JOIN moradores/visitantes)
 *   - Por Morador          (agrupado por morador, com contagem de entradas/saídas)
 *   - Visitantes           (eventos de visitantes com status de autorização)
 *   - Acessos Negados      (denied_access_log)
 *   - Por Unidade          (agrupado por unidade, todos os moradores e veículos)
 *
 * Funcionalidades:
 *   - Evidências fotográficas (miniatura clicável → modal tela cheia)
 *   - Paginação com padrão 20 registros, seletor 10/20/50/100
 *   - Filtros: data, hora, câmera, tipo de acesso, busca livre
 *   - Sumário de cards por aba
 *   - Exportação CSV
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { hojeLocal } from '../utils/dataLocal'; // Onda 138: data local, não UTC
import { useAuth } from '../context/AuthContext';
import { descritorDaVertical } from '../data/marca';
import Pagination from '../components/Pagination';
// Onda 108: a frequência por veículo é uma tela à parte — filtros próprios,
// consulta explícita e outro endpoint. Fica como aba, mas não passa pelo
// `fetchData` das demais.
import FrequenciaVeiculos from '../components/FrequenciaVeiculos';
import { toast } from 'react-hot-toast';
import {
  FiSearch, FiX, FiDownload, FiBarChart2,
  FiUsers, FiUserPlus, FiAlertCircle, FiCalendar,
  FiRefreshCw, FiCheckCircle, FiXCircle, FiClock,
  FiCamera, FiHome, FiRepeat
} from 'react-icons/fi';
import './AccessReports.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR');
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR');
}

function evidenceUrl(imagePath) {
  if (!imagePath) return null;
  const token = localStorage.getItem('token');
  return `/api/evidence/${imagePath}?token=${encodeURIComponent(token)}`;
}

const todayISO = hojeLocal();

// Onda 135: a permanência de uma saída (minutos desde a última entrada lida da mesma placa).
export function textoPermanencia(min) {
  if (min === null || min === undefined || min === '') return null;
  const m = Number(min);
  if (!Number.isFinite(m) || m < 0) return null;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${String(r).padStart(2, '0')}` : `${h} h`;
}

// A célula da permanência: só nas saídas; "entrada não lida" quando não há de onde contar.
const Permanencia = ({ r, toleranciaMin }) => {
  if (r.log_type !== 'saida') return <span className="evidence-none">—</span>;
  const texto = textoPermanencia(r.stay_minutes);
  if (!texto) return <span className="stay-unknown" title="Não há entrada lida desta placa nas 24 h anteriores">entrada não lida</span>;
  return (
    <span className="stay-cell">
      <span className="stay-time">{texto}</span>
      {r.within_tolerance === 1 && (
        <span className="tol-badge" title={`Saída até ${toleranciaMin} min depois da entrada`}>na tolerância</span>
      )}
    </span>
  );
};

// ─── Componente de Paginação ──────────────────────────────────────────────────

// ─── Componente de Miniatura de Evidência ─────────────────────────────────────
const EvidenceThumb = ({ imagePath, onOpen }) => {
  const url = evidenceUrl(imagePath);
  if (!url) return <span className="evidence-none">—</span>;
  return (
    <button className="btn-evidence" onClick={() => onOpen(url)} title="Ver evidência fotográfica">
      <FiCamera size={14} />
    </button>
  );
};

// ─── Modal de Evidência ───────────────────────────────────────────────────────
const EvidenceModal = ({ url, onClose }) => {
  if (!url) return null;
  return (
    <div className="evidence-modal-overlay" onClick={onClose}>
      <div className="evidence-modal-box" onClick={e => e.stopPropagation()}>
        <div className="evidence-modal-header">
          <span><FiCamera /> Evidência Fotográfica</span>
          <button className="evidence-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <img src={url} alt="Evidência fotográfica" className="evidence-modal-img" />
      </div>
    </div>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
export default function AccessReports() {
  // A vertical decide qual cadastro é cruzado com os acessos: moradores e
  // unidades no condomínio, associados no clube. Os acessos em si vêm da mesma
  // tabela `plate_logs` nos dois casos.
  const { vertical, can } = useAuth();
  const ehClube = vertical === 'clube_social';
  // A aba só aparece para quem a rota aceita — o mesmo catálogo que barra a
  // requisição no servidor (Onda 108).
  const temFrequencia = can('plate_frequency');
  const [tab, setTab]           = useState('movement');
  const [data, setData]         = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [cameras, setCameras]   = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [erro, setErro]               = useState(null);
  // Onda 135: a tolerância vem do servidor junto com o relatório (é parâmetro de Configurações).
  const [toleranciaMin, setToleranciaMin] = useState(15);
  // Onda 178: a saída livre na tolerância desligada — a tela esconde selo, cartão, filtro e coluna.
  const [toleranciaLigada, setToleranciaLigada] = useState(true);
  // A busca digitada não pode disparar uma requisição por tecla.
  const [buscaDigitada, setBuscaDigitada] = useState('');
  const abortRef = useRef(null);
  const [evidenceUrl_modal, setEvidenceUrlModal] = useState(null);

  const [filters, setFilters] = useState({
    date_from:   todayISO,
    date_to:     todayISO,
    time_from:   '',
    time_to:     '',
    camera_id:   '',
    access_type: '',
    search:      '',
    tolerance:   ''      // Onda 135: '1' = só saídas na tolerância
  });

  // Carregar câmeras
  const fetchCameras = useCallback(async () => {
    try {
      const res = await api.get('/api/cameras');
      setCameras(res.data.data || []);
    } catch (e) {
      // Sem a lista, o filtro de câmera fica vazio — vale dizer por quê.
      toast.error('Não foi possível carregar a lista de câmeras');
    }
  }, []);

  useEffect(() => { fetchCameras(); }, [fetchCameras]);

  // As abas agrupadas trazem uma linha por morador ou por unidade e não paginam
  // no servidor: o número de moradores não cresce com o período consultado.
  const paginaNoServidor = tab === 'movement' || tab === 'visitors' || tab === 'denied';

  const fetchData = useCallback(async (pagina = 1, tamanho = pageSize) => {
    // A aba de frequência tem consulta própria e explícita: entrar nela não
    // pode disparar a busca das outras (Onda 108).
    if (tab === 'frequency') return;
    // Uma resposta antiga chegando depois da nova sobrescrevia o resultado
    // certo — o efeito clássico de busca sem cancelamento.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      if (paginaNoServidor) {
        params.limit = tamanho;
        params.offset = (pagina - 1) * tamanho;
      }
      const endpoint =
        tab === 'movement'  ? '/api/access-reports/movement'
      : tab === 'residents' ? '/api/access-reports/by-resident'
      : tab === 'members'   ? '/api/access-reports/by-member'
      : tab === 'visitors'  ? '/api/access-reports/visitors'
      : tab === 'denied'    ? '/api/access-reports/denied'
      :                       '/api/access-reports/by-unit';
      const res = await api.get(endpoint, { params, signal: controller.signal });
      const linhas = res.data.data || [];
      setData(linhas);
      setSummary(res.data.summary || null);
      if (Number.isFinite(Number(res.data.tolerance_min)) && Number(res.data.tolerance_min) > 0) setToleranciaMin(Number(res.data.tolerance_min));
      setToleranciaLigada(res.data.tolerance_enabled !== false);
      setTotalRecords(paginaNoServidor ? (res.data.total || 0) : linhas.length);
      setCurrentPage(pagina);
      setErro(null);
    } catch (e) {
      if (e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
      const msg = e.response?.data?.message || 'Não foi possível carregar o relatório.';
      setErro(msg);
      setData([]);
      setSummary(null);
      setTotalRecords(0);
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  }, [tab, filters, pageSize, paginaNoServidor]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  // Debounce da busca: o texto digitado só vira filtro depois de meio segundo
  // parado. Antes, procurar por uma placa disparava sete requisições.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(f => (f.search === buscaDigitada ? f : { ...f, search: buscaDigitada }));
    }, 500);
    return () => clearTimeout(t);
  }, [buscaDigitada]);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    if (paginaNoServidor) fetchData(1, newSize);
  };

  const handlePageChange = (pagina) => {
    if (paginaNoServidor) fetchData(pagina);
    else setCurrentPage(pagina);
  };

  // Nas abas paginadas pelo servidor, a página já vem pronta. Nas agrupadas
  // (por morador, por unidade), o recorte continua no navegador.
  const totalPages = Math.max(1, Math.ceil((paginaNoServidor ? totalRecords : data.length) / pageSize));
  const pagedData  = paginaNoServidor
    ? data
    : data.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // O CSV vem do servidor, com o período inteiro. Montado no navegador, ele
  // exportaria apenas a página visível — e saía com vírgula e sem BOM, o que
  // fazia o Excel em português jogar tudo numa coluna e quebrar a acentuação.
  const exportCSV = async () => {
    if (!['movement', 'visitors', 'denied'].includes(tab)) {
      toast.error('Exportação disponível nas abas Movimentação, Visitantes e Negados');
      return;
    }
    try {
      const params = { ...filters, tab };
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await api.get('/api/access-reports/export.csv', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `acesso_${tab}_${filters.date_from}_${filters.date_to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Não foi possível exportar o relatório');
    }
  };

  return (
    <div className="access-reports-page">
      {/* Modal de Evidência */}
      <EvidenceModal url={evidenceUrl_modal} onClose={() => setEvidenceUrlModal(null)} />

      {/* Cabeçalho */}
      <div className="page-header">
        <div>
          <h1>📊 Relatórios de Acesso</h1>
          {/* Onda 108b: o subtítulo dizia "Vertical Condomínio" em qualquer
              instalação — inclusive no clube, que é outra vertical. */}
          <p>Histórico e análise de movimentação — Vertical {descritorDaVertical(vertical) || 'Condomínio'}</p>
        </div>
        {/* A frequência por veículo traz os próprios botões, junto dos seus
            filtros: o "Atualizar" daqui não teria o que atualizar. */}
        {tab !== 'frequency' && (
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
              <FiRefreshCw className={loading ? 'spin' : ''} /> Atualizar
            </button>
            <button className="btn btn-secondary" onClick={exportCSV} disabled={!data.length}>
              <FiDownload /> Exportar CSV
            </button>
          </div>
        )}
      </div>

      {/* Abas — as duas verticais leem os mesmos acessos; muda o cadastro
          cruzado. O condomínio agrupa por morador e por unidade; o clube, por
          associado, que não tem bloco nem unidade. */}
      <div className="tabs">
        <button className={`tab ${tab === 'movement'  ? 'active' : ''}`} onClick={() => handleTabChange('movement')}>
          <FiBarChart2 /> Movimentação Geral
        </button>
        {ehClube ? (
          <button className={`tab ${tab === 'members' ? 'active' : ''}`} onClick={() => handleTabChange('members')}>
            <FiUsers /> Por Associado
          </button>
        ) : (
          <button className={`tab ${tab === 'residents' ? 'active' : ''}`} onClick={() => handleTabChange('residents')}>
            <FiUsers /> Por Morador
          </button>
        )}
        <button className={`tab ${tab === 'visitors'  ? 'active' : ''}`} onClick={() => handleTabChange('visitors')}>
          <FiUserPlus /> Visitantes
        </button>
        <button className={`tab ${tab === 'denied'    ? 'active' : ''}`} onClick={() => handleTabChange('denied')}>
          <FiAlertCircle /> Acessos Negados
        </button>
        {!ehClube && (
          <button className={`tab ${tab === 'by-unit'   ? 'active' : ''}`} onClick={() => handleTabChange('by-unit')}>
            <FiHome /> Por Unidade
          </button>
        )}
        {temFrequencia && (
          <button className={`tab ${tab === 'frequency' ? 'active' : ''}`} onClick={() => handleTabChange('frequency')}>
            <FiRepeat /> Frequência por Veículo
          </button>
        )}
      </div>

      {tab === 'frequency' ? <FrequenciaVeiculos ehClube={ehClube} /> : (
      <>

      {/* Filtros */}
      <div className="filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <label><FiCalendar /> Data Inicial</label>
            <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} />
          </div>
          <div className="filter-group">
            <label><FiCalendar /> Data Final</label>
            <input type="date" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} />
          </div>
          <div className="filter-group">
            <label><FiClock /> Horário Inicial</label>
            <input type="time" value={filters.time_from} onChange={e => setFilter('time_from', e.target.value)} />
          </div>
          <div className="filter-group">
            <label><FiClock /> Horário Final</label>
            <input type="time" value={filters.time_to} onChange={e => setFilter('time_to', e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Câmera</label>
            <select value={filters.camera_id} onChange={e => setFilter('camera_id', e.target.value)}>
              <option value="">Todas</option>
              {cameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {tab === 'movement' && (
            <div className="filter-group">
              <label>Tipo de Acesso</label>
              {/* Onda 114: cada vertical oferece as classificações que o motor
                  grava nela. O clube oferecia "Morador", que ali não existe, e
                  não oferecia "Associado". */}
              <select value={filters.access_type} onChange={e => setFilter('access_type', e.target.value)}>
                <option value="">Todos</option>
                {ehClube ? (
                  <>
                    <option value="associado_clube">Associado</option>
                    <option value="associado_inativo">Associado inativo</option>
                    <option value="visitante">Visitante</option>
                  </>
                ) : (
                  <>
                    <option value="morador">Morador</option>
                    <option value="visitante">Visitante</option>
                    <option value="negado">Negado</option>
                    <option value="avulso">Avulso</option>
                  </>
                )}
              </select>
            </div>
          )}
          {(tab === 'movement' || tab === 'visitors') && (
            <div className="filter-group">
              <label>Permanência</label>
              {/* Onda 135: só saídas lidas até a tolerância depois da entrada. */}
              <select value={filters.tolerance} onChange={e => setFilter('tolerance', e.target.value)}>
                <option value="">Todas</option>
                {toleranciaLigada && <option value="1">Só saídas na tolerância ({toleranciaMin} min)</option>}
              </select>
            </div>
          )}
          <div className="filter-group search-filter">
            <label>Buscar</label>
            <div className="search-box">
              <FiSearch />
              <input
                placeholder={
                  tab === 'by-unit' ? 'Bloco, unidade, morador...'
                  : tab === 'members' ? 'Placa, nome, código do associado...'
                  : ehClube ? 'Placa, nome do associado...'
                  : 'Placa, nome, unidade...'
                }
                value={buscaDigitada}
                onChange={e => setBuscaDigitada(e.target.value)}
              />
              {buscaDigitada && <button onClick={() => setBuscaDigitada('')} aria-label="Limpar busca"><FiX /></button>}
            </div>
          </div>
        </div>
      </div>

      {/* Sumário */}
      {summary && (
        <div className="summary-cards">
          {/* Movimentação Geral */}
          {summary.total !== undefined && tab === 'movement' && (
            <>
              <div className="summary-card">
                <div className="summary-value">{summary.total}</div>
                <div className="summary-label">Total de Registros</div>
              </div>
              {/* Onda 114: no clube o cartão conta ASSOCIADOS. Contava moradores,
                  que ali não existem — e o acesso do associado não entrava em
                  cartão nenhum. */}
              <div className="summary-card success">
                <div className="summary-value">{ehClube ? (summary.members ?? 0) : summary.residents}</div>
                <div className="summary-label">{ehClube ? 'Associados' : 'Moradores'}</div>
              </div>
              <div className="summary-card info">
                <div className="summary-value">{summary.visitors}</div>
                <div className="summary-label">Visitantes</div>
              </div>
              <div className="summary-card danger">
                <div className="summary-value">{ehClube ? (summary.inactive_members ?? 0) : summary.denied}</div>
                <div className="summary-label">{ehClube ? 'Associados inativos' : 'Acessos Negados'}</div>
              </div>
              {/* Onda 135 */}
              {toleranciaLigada && summary.exits !== undefined && (
                <div className="summary-card tolerance">
                  <div className="summary-value">{summary.exits_in_tolerance ?? 0}<span className="summary-of"> de {summary.exits}</span></div>
                  <div className="summary-label">Saídas na tolerância ({toleranciaMin} min){summary.exits ? ` · ${Math.round(100 * (summary.exits_in_tolerance || 0) / summary.exits)}%` : ''}</div>
                </div>
              )}
            </>
          )}
          {/* Por Associado */}
          {tab === 'members' && summary.total !== undefined && (
            <>
              <div className="summary-card">
                <div className="summary-value">{summary.total}</div>
                <div className="summary-label">Veículos de Associados</div>
              </div>
              <div className="summary-card success">
                <div className="summary-value">{summary.with_access}</div>
                <div className="summary-label">Com Acesso no Período</div>
              </div>
              <div className="summary-card">
                <div className="summary-value">{summary.without_access}</div>
                <div className="summary-label">Sem Acesso no Período</div>
              </div>
              <div className="summary-card">
                <div className="summary-value">{summary.inactive}</div>
                <div className="summary-label">Associado ou Veículo Inativo</div>
              </div>
              {toleranciaLigada && summary.exits !== undefined && (
                <div className="summary-card tolerance">
                  <div className="summary-value">{summary.exits_in_tolerance ?? 0}<span className="summary-of"> de {summary.exits}</span></div>
                  <div className="summary-label">Saídas na tolerância ({toleranciaMin} min)</div>
                </div>
              )}
            </>
          )}
          {/* Por Morador */}
          {tab === 'residents' && summary.total !== undefined && (
            <>
              <div className="summary-card">
                <div className="summary-value">{summary.total}</div>
                <div className="summary-label">Moradores Cadastrados</div>
              </div>
              <div className="summary-card success">
                <div className="summary-value">{summary.with_access}</div>
                <div className="summary-label">Com Acesso no Período</div>
              </div>
              <div className="summary-card">
                <div className="summary-value">{summary.without_access}</div>
                <div className="summary-label">Sem Acesso no Período</div>
              </div>
              {toleranciaLigada && summary.exits !== undefined && (
                <div className="summary-card tolerance">
                  <div className="summary-value">{summary.exits_in_tolerance ?? 0}<span className="summary-of"> de {summary.exits}</span></div>
                  <div className="summary-label">Saídas na tolerância ({toleranciaMin} min)</div>
                </div>
              )}
            </>
          )}
          {/* Visitantes */}
          {tab === 'visitors' && summary.total !== undefined && (
            <>
              <div className="summary-card">
                <div className="summary-value">{summary.total}</div>
                <div className="summary-label">Total de Acessos</div>
              </div>
              {/* Onda 148: o resultado na hora do acesso, pelas autorizações de visita */}
              <div className="summary-card success">
                <div className="summary-value">{summary.authorized}</div>
                <div className="summary-label">Autorizados</div>
              </div>
              <div className="summary-card ar-sem-autorizacao">
                <div className="summary-value">{summary.unauthorized ?? 0}</div>
                <div className="summary-label">Sem autorização</div>
              </div>
              <div className="summary-card ar-vencida">
                <div className="summary-value">{summary.expired}</div>
                <div className="summary-label">Autorização vencida</div>
              </div>
              {toleranciaLigada && summary.exits !== undefined && (
                <div className="summary-card tolerance">
                  <div className="summary-value">{summary.exits_in_tolerance ?? 0}<span className="summary-of"> de {summary.exits}</span></div>
                  <div className="summary-label">Saídas na tolerância ({toleranciaMin} min)</div>
                </div>
              )}
            </>
          )}
          {/* Acessos Negados */}
          {tab === 'denied' && summary.total !== undefined && (
            <>
              <div className="summary-card danger">
                <div className="summary-value">{summary.total}</div>
                <div className="summary-label">Total Negados</div>
              </div>
              <div className="summary-card">
                <div className="summary-value">{summary.pending_ack}</div>
                <div className="summary-label">Pendentes de Confirmação</div>
              </div>
              <div className="summary-card success">
                <div className="summary-value">{summary.acknowledged}</div>
                <div className="summary-label">Confirmados</div>
              </div>
            </>
          )}
          {/* Por Unidade */}
          {tab === 'by-unit' && summary.total_units !== undefined && (
            <>
              <div className="summary-card">
                <div className="summary-value">{summary.total_units}</div>
                <div className="summary-label">Unidades Cadastradas</div>
              </div>
              <div className="summary-card success">
                <div className="summary-value">{summary.units_with_access}</div>
                <div className="summary-label">Com Acesso no Período</div>
              </div>
              <div className="summary-card info">
                <div className="summary-value">{summary.total_entries}</div>
                <div className="summary-label">Total de Entradas</div>
              </div>
              <div className="summary-card">
                <div className="summary-value">{summary.total_exits}</div>
                <div className="summary-label">Total de Saídas</div>
              </div>
              {toleranciaLigada && summary.exits_in_tolerance !== undefined && (
                <div className="summary-card tolerance">
                  <div className="summary-value">{summary.exits_in_tolerance}</div>
                  <div className="summary-label">Saídas na tolerância ({toleranciaMin} min)</div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Tabela de resultados */}
      {erro && (
        <div className="ar-error" role="alert">
          {erro}
          <button className="btn btn-sm btn-secondary" onClick={() => fetchData(1)}>Tentar de novo</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">Carregando relatório...</div>
      ) : data.length === 0 ? (
        <div className="empty-state">
          <FiBarChart2 size={48} />
          <h3>Nenhum registro encontrado</h3>
          <p>Ajuste os filtros e tente novamente.</p>
        </div>
      ) : (
        <>
          {/* Paginação superior */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalRecords={paginaNoServidor ? totalRecords : data.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />

          <div className="table-wrapper">

            {/* ── Movimentação Geral ── */}
            {tab === 'movement' && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>📷</th>
                    <th>Data/Hora</th>
                    <th>Placa</th>
                    <th>Tipo</th>
                    <th>Direção</th>
                    <th>Nome / Unidade</th>
                    <th>Permanência</th>
                    <th>Câmera</th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((r, i) => (
                    <tr key={i}>
                      <td className="evidence-cell">
                        <EvidenceThumb imagePath={r.image_path} onOpen={setEvidenceUrlModal} />
                      </td>
                      <td>{formatDateTime(r.detected_at)}</td>
                      <td><span className="plate-mono">{r.plate}</span></td>
                      <td>
                        <span className={`type-badge type-${r.access_type}`}>
                          {r.access_type === 'morador'    ? '🏠 Morador'
                         : r.access_type === 'associado_clube'   ? '🎫 Associado'
                         : r.access_type === 'associado_inativo' ? '⚠ Associado inativo'
                         : r.access_type === 'visitante'  ? '👤 Visitante'
                         : r.access_type === 'negado'     ? '⛔ Negado'
                         : r.access_type === 'mensalista' ? '📋 Mensalista'
                         : r.access_type === 'credenciado'? '🔑 Credenciado'
                         : '🚗 Avulso'}
                        </span>
                      </td>
                      <td>
                        <span className={`direction-badge direction-${r.log_type}`}>
                          {r.log_type === 'entrada' ? '⬆ Entrada' : '⬇ Saída'}
                        </span>
                      </td>
                      <td>
                        <div>{r.person_name || '—'}</div>
                        {r.unit && <div className="unit-label">{r.block ? `Bloco ${r.block} / ` : ''}Unid. {r.unit}</div>}
                      </td>
                      <td><Permanencia r={r} toleranciaMin={toleranciaMin} /></td>
                      <td>{r.camera_name || '—'}</td>
                      <td>
                        {r.access_granted
                          ? <span className="result-ok"><FiCheckCircle /> Liberado</span>
                          : <span className="result-denied"><FiXCircle /> Negado</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ── Por Morador ── */}
            {tab === 'residents' && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>📷 Entrada</th>
                    <th>📷 Saída</th>
                    <th>Morador</th>
                    <th>Unidade</th>
                    <th>Placa</th>
                    <th>Veículo</th>
                    <th>Entradas</th>
                    <th>Saídas</th>
                    {toleranciaLigada && <th title={`Saídas até ${toleranciaMin} min depois da entrada`}>Na tolerância</th>}
                    <th>Último Acesso</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((r, i) => (
                    <tr key={i} className={!r.last_access ? 'row-no-access' : ''}>
                      <td className="evidence-cell">
                        <EvidenceThumb imagePath={r.last_entry_image} onOpen={setEvidenceUrlModal} />
                      </td>
                      <td className="evidence-cell">
                        <EvidenceThumb imagePath={r.last_exit_image} onOpen={setEvidenceUrlModal} />
                      </td>
                      <td>
                        <div className="resident-name">{r.resident_name}</div>
                        <span className={`status-badge status-${r.resident_status}`}>{r.resident_status}</span>
                      </td>
                      <td>
                        {r.block ? <span className="unit-label">Bloco {r.block} / </span> : ''}
                        Unid. {r.unit}
                      </td>
                      <td><span className="plate-mono">{r.plate}</span></td>
                      <td>
                        <div>{r.brand} {r.model}</div>
                        {r.color && <div className="unit-label">{r.color}{r.year ? ` · ${r.year}` : ''}</div>}
                      </td>
                      <td className="text-center count-cell">{r.entries || 0}</td>
                      <td className="text-center count-cell">{r.exits || 0}</td>
                      {toleranciaLigada && <td className="text-center count-cell">{Number(r.exits_in_tolerance) > 0 ? <span className="tol-badge">{r.exits_in_tolerance}</span> : '0'}</td>}
                      <td>{r.last_access ? formatDateTime(r.last_access) : <span className="no-access-label">Sem acesso</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ── Por Associado (Clube Social) ── */}
            {tab === 'members' && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>📷</th>
                    <th>📷</th>
                    <th>Associado</th>
                    <th>Código</th>
                    <th>Placa</th>
                    <th>Veículo</th>
                    <th>Entradas</th>
                    <th>Saídas</th>
                    {toleranciaLigada && <th title={`Saídas até ${toleranciaMin} min depois da entrada`}>Na tolerância</th>}
                    <th>Último Acesso</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((r, i) => (
                    <tr key={i} className={!r.last_access ? 'row-no-access' : ''}>
                      <td className="evidence-cell">
                        <EvidenceThumb imagePath={r.last_entry_image} onOpen={setEvidenceUrlModal} />
                      </td>
                      <td className="evidence-cell">
                        <EvidenceThumb imagePath={r.last_exit_image} onOpen={setEvidenceUrlModal} />
                      </td>
                      <td>
                        <div className="resident-name">{r.member_name}</div>
                        <span className={`status-badge status-${r.member_status === 'active' ? 'ativo' : 'inativo'}`}>
                          {r.member_status === 'active' ? 'ativo' : 'inativo'}
                        </span>
                      </td>
                      <td><span className="plate-mono">{r.member_code}</span></td>
                      <td><span className="plate-mono">{r.plate}</span></td>
                      <td>
                        <div>{[r.brand, r.model].filter(Boolean).join(' ') || '—'}</div>
                        {r.vehicle_status === 'inactive' && <div className="unit-label">veículo inativo</div>}
                      </td>
                      <td className="text-center count-cell">{r.entries || 0}</td>
                      <td className="text-center count-cell">{r.exits || 0}</td>
                      {toleranciaLigada && <td className="text-center count-cell">{Number(r.exits_in_tolerance) > 0 ? <span className="tol-badge">{r.exits_in_tolerance}</span> : '0'}</td>}
                      <td>{r.last_access ? formatDateTime(r.last_access) : <span className="no-access-label">Sem acesso</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ── Visitantes ── */}
            {tab === 'visitors' && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>📷</th>
                    <th>Data/Hora</th>
                    <th>Visitante</th>
                    <th>Placa</th>
                    <th>Morador / Unidade</th>
                    <th>Finalidade</th>
                    <th>Permanência</th>
                    <th>Câmera</th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((r, i) => (
                    <tr key={i}>
                      <td className="evidence-cell">
                        <EvidenceThumb imagePath={r.image_path} onOpen={setEvidenceUrlModal} />
                      </td>
                      <td>{formatDateTime(r.detected_at)}</td>
                      <td>
                        <div>{r.visitor_name || '—'}</div>
                        {r.valid_until && (
                          <div className="unit-label">Válido até {formatDate(r.valid_until)}</div>
                        )}
                      </td>
                      <td><span className="plate-mono">{r.plate}</span></td>
                      <td>
                        <div>{r.resident_name || '—'}</div>
                        {r.unit && <div className="unit-label">{r.block ? `Bloco ${r.block} / ` : ''}Unid. {r.unit}</div>}
                      </td>
                      <td>{r.purpose || '—'}</td>
                      <td><Permanencia r={r} toleranciaMin={toleranciaMin} /></td>
                      <td>{r.camera_name || '—'}</td>
                      <td>
                        {/* Onda 148: avaliado na hora do acesso, não pelo cadastro de hoje */}
                        {r.access_result === 'autorizado'
                          ? <span className="result-ok"><FiCheckCircle /> Autorizado</span>
                          : r.access_result === 'vencida'
                            ? <span className="ar-result-vencida" title="A placa tinha autorização, mas não valia na hora deste acesso"><FiXCircle /> Autorização vencida</span>
                            : <span className="ar-result-sem" title="A placa não tinha autorização de visita até este acesso"><FiXCircle /> Sem autorização</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ──Acessos Negados ── */}
            {tab === 'denied' && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>📷</th>
                    <th>Data/Hora</th>
                    <th>Placa</th>
                    <th>Câmera</th>
                    <th>Direção</th>
                    <th>Motivo</th>
                    <th>Confirmado</th>
                    <th>Confirmado em</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((r, i) => (
                    <tr key={i} className={r.operator_ack ? '' : 'row-unack'}>
                      <td className="evidence-cell">
                        <EvidenceThumb imagePath={r.image_path} onOpen={setEvidenceUrlModal} />
                      </td>
                      <td>{formatDateTime(r.detected_at)}</td>
                      <td><span className="plate-mono">{r.plate}</span></td>
                      <td>{r.camera_name || '—'}</td>
                      <td>
                        <span className={`direction-badge direction-${r.log_type}`}>
                          {r.log_type === 'entrada' ? '⬆ Entrada' : '⬇ Saída'}
                        </span>
                      </td>
                      <td><span className="denied-reason">{r.reason_label || r.reason}</span></td>
                      <td>
                        {r.operator_ack
                          ? <span className="result-ok"><FiCheckCircle /> Sim</span>
                          : <span className="result-denied"><FiXCircle /> Pendente</span>}
                      </td>
                      <td>{r.ack_at ? formatDateTime(r.ack_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* ── Por Unidade ── */}
            {tab === 'by-unit' && (
              <table className="report-table">
                <thead>
                  <tr>
                    <th>📷 Último Acesso</th>
                    <th>Unidade</th>
                    <th>Moradores</th>
                    <th>Placas Cadastradas</th>
                    <th>Entradas</th>
                    <th>Saídas</th>
                    {toleranciaLigada && <th title={`Saídas até ${toleranciaMin} min depois da entrada`}>Na tolerância</th>}
                    <th>Último Acesso</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((r, i) => (
                    <tr key={i} className={!r.last_access ? 'row-no-access' : ''}>
                      <td className="evidence-cell">
                        <EvidenceThumb imagePath={r.last_entry_image} onOpen={setEvidenceUrlModal} />
                      </td>
                      <td>
                        <div className="unit-main">
                          {r.block ? <span className="block-badge">Bloco {r.block}</span> : null}
                          <span className="unit-number">Unid. {r.unit}</span>
                        </div>
                        <div className="unit-meta">
                          {r.resident_count} morador{r.resident_count !== 1 ? 'es' : ''} · {r.vehicle_count} veículo{r.vehicle_count !== 1 ? 's' : ''}
                        </div>
                      </td>
                      <td>
                        <div className="ar-residents-list">{r.residents_names || '—'}</div>
                      </td>
                      <td>
                        <div className="plates-list">
                          {(r.plates || '').split(', ').map((p, pi) => (
                            <span key={pi} className="plate-mono plate-small">{p}</span>
                          ))}
                        </div>
                      </td>
                      <td className="text-center count-cell">{r.entries || 0}</td>
                      <td className="text-center count-cell">{r.exits || 0}</td>
                      {toleranciaLigada && <td className="text-center count-cell">{Number(r.exits_in_tolerance) > 0 ? <span className="tol-badge">{r.exits_in_tolerance}</span> : '0'}</td>}
                      <td>{r.last_access ? formatDateTime(r.last_access) : <span className="no-access-label">Sem acesso</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

          </div>

          {/* Paginação inferior */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalRecords={paginaNoServidor ? totalRecords : data.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
      </>
      )}
    </div>
  );
}
